import { RechargeSpinDerivedState, RechargeSpinInfo } from "./types";

export const clampProgressPercent = (
  totalExperience?: number,
  drawExperience?: number
) => {
  if (!drawExperience || drawExperience <= 0) {
    return 0;
  }

  return Math.max(
    0,
    Math.min(100, Math.floor(((totalExperience || 0) / drawExperience) * 100))
  );
};

const getNextProgressExperience = (info: RechargeSpinInfo | undefined) => {
  const drawExperience = info?.drawExperience || 0;
  const totalExperience = info?.totalExperience || 0;

  if (drawExperience <= 0) {
    return 0;
  }

  if ((info?.availableDrawTimes || 0) > 0) {
    return drawExperience;
  }

  return totalExperience % drawExperience;
};

const buildDerivedState = (
  info: RechargeSpinInfo | undefined,
  availability: RechargeSpinDerivedState["availability"],
  isReady: boolean,
  isLocked: boolean
): RechargeSpinDerivedState => {
  const drawExperience = info?.drawExperience || 0;
  const progressExperience = getNextProgressExperience(info);

  return {
    availability,
    progressPercent: clampProgressPercent(progressExperience, drawExperience),
    progressExperience,
    drawExperience,
    isReady,
    isLocked,
  };
};

export const parseRechargeSpinTimestamp = (value?: string | null) => {
  const source = value?.trim();

  if (!source) {
    return 0;
  }

  const localDateTimeMatch = source.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2})(?::(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,3}))?)?)?)?$/
  );

  if (localDateTimeMatch) {
    const [, year, month, day, hour = "0", minute = "0", second = "0", ms = "0"] =
      localDateTimeMatch;
    const timestamp = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      Number(ms.padEnd(3, "0"))
    ).getTime();

    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  const timestamp = new Date(source).getTime();

  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getRechargeSpinRemainingExperience = (
  derived: RechargeSpinDerivedState
) => {
  if (derived.drawExperience <= 0) {
    return 0;
  }

  return Math.max(0, derived.drawExperience - derived.progressExperience);
};

export const deriveRechargeSpinState = (
  info: RechargeSpinInfo | undefined,
  now = Date.now()
): RechargeSpinDerivedState => {
  if (!info || info.status !== 1) {
    return buildDerivedState(info, "closed", false, true);
  }

  const startTime = parseRechargeSpinTimestamp(info.startTime);
  const endTime = parseRechargeSpinTimestamp(info.endTime);

  if (startTime && now < startTime) {
    return buildDerivedState(info, "notStarted", false, true);
  }

  if (endTime && now > endTime) {
    return buildDerivedState(info, "ended", false, true);
  }

  if (
    Number.isFinite(info.dailyDrawLimit) &&
    info.dailyDrawLimit > 0 &&
    info.todayDrawTimes >= info.dailyDrawLimit
  ) {
    return buildDerivedState(info, "dailyLimit", false, true);
  }

  if (info.availableDrawTimes > 0) {
    return buildDerivedState(info, "ready", true, false);
  }

  return buildDerivedState(info, "locked", false, true);
};
